import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatAppConstants } from '../chat-app.constants';
import { ChatAppTelemetryService } from './chat-app-telemetry.service';

/** How this device can install the app. */
export type ChatAppInstallMode =
  /** The browser offers a native prompt (Chromium on desktop and Android). */
  | 'prompt'
  /** iPhone / iPad Safari: Share → Add to Home Screen. */
  | 'ios'
  /** macOS Safari: File → Add to Dock. */
  | 'macSafari'
  /** Already running as an installed app, or no known way to install. */
  | 'none';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * The install invitation — SPEC-2026-09-internal-chat-pwa (T-36 · RF-14 · RF-15 · RF-28).
 *
 * Captures the browser's install prompt where one exists; on platforms that only allow a
 * manual install, the app shows the steps instead. The invitation is offered inside the
 * chat app only, and a dismissal is remembered per device.
 */
@Injectable({ providedIn: 'root' })
export class ChatAppInstallService {

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  private readonly mode = new BehaviorSubject<ChatAppInstallMode>('none');

  readonly mode$: Observable<ChatAppInstallMode> = this.mode.asObservable();

  private started = false;

  constructor(
    private readonly zone: NgZone,
    private readonly telemetry: ChatAppTelemetryService,
  ) { }

  start(): void {
    if (this.started) return;
    this.started = true;

    if (this.isStandalone()) {
      // Launched as the installed app: that is the install signal on every platform,
      // including the manual ones where no `appinstalled` event fires.
      this.telemetry.reportInstall();
      return;
    }

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.zone.run(() => {
        this.deferredPrompt = event as BeforeInstallPromptEvent;
        this.mode.next('prompt');
      });
    });

    window.addEventListener('appinstalled', () => this.zone.run(() => {
      this.deferredPrompt = null;
      this.mode.next('none');
      this.telemetry.reportInstall();
    }));

    this.mode.next(this.detectManualMode());
  }

  get currentMode(): ChatAppInstallMode {
    return this.mode.value;
  }

  get dismissed(): boolean {
    try {
      return !!localStorage.getItem(ChatAppConstants.installInvitationDismissed);
    } catch {
      return false;
    }
  }

  dismiss(): void {
    try {
      localStorage.setItem(ChatAppConstants.installInvitationDismissed, new Date().toISOString());
    } catch {
      /* private mode — the invitation simply comes back next time */
    }
  }

  /** Opens the native prompt. Resolves true when the user accepted. */
  async promptInstall(): Promise<boolean> {
    const prompt = this.deferredPrompt;
    if (!prompt) return false;
    this.deferredPrompt = null;
    await prompt.prompt();
    const choice = await prompt.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
    if (choice.outcome !== 'accepted') {
      // A dismissed native prompt cannot be shown again until the browser re-offers it.
      this.mode.next(this.detectManualMode());
    }
    return choice.outcome === 'accepted';
  }

  isStandalone(): boolean {
    const nav = navigator as Navigator & { standalone?: boolean };
    return (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) || nav.standalone === true;
  }

  private detectManualMode(): ChatAppInstallMode {
    const userAgent = navigator.userAgent ?? '';
    const isIos = /iPhone|iPad|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIos) return 'ios';
    const isSafari = /Safari/.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/.test(userAgent);
    if (isSafari && /Macintosh/.test(userAgent)) return 'macSafari';
    return 'none';
  }
}
